import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Container, Section, Text10, Text12, Text14, Text16, Wrapper } from '../../atoms';
import { Intro, Loading } from '../../molecules';

const SEND_APPLICATION = gql`
  mutation ($name: String!, $email: String!, $motivation: String, $cv: Upload!, $website: String) {
    customCareerApplicationSendEmail(name: $name, email: $email, motivation: $motivation, cv: $cv, website: $website)
  }
`;

/**
 * Trabalhe connosco. The cv goes straight to the backend as an upload and is
 * forwarded by email - it is never stored on the site.
 */
export default function CareersForm({
  title,
  nameLabel,
  emailLabel,
  cvLabel,
  cvPlaceholder,
  cvEmptyLabel,
  motivationLabel,
  sendLabel,
  infoText,
  successText,
  errorText,
  cvRequiredText,
}) {
  const [inputs, setInputs] = useState({});
  const [cv, setCv] = useState(null);
  const [missCv, setMissCv] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  // a hidden field a person never sees, so anything in it came from a bot
  const [website, setWebsite] = useState('');

  const fileInput = useRef(null);

  const handleInput = (event) => {
    event.persist();
    setInputs((prevInputs) => ({ ...prevInputs, [event.target.name]: event.target.value }));
  };

  const handleFile = (event) => {
    const file = event.target.files && event.target.files[0];
    setCv(file || null);
    if (file) setMissCv(false);
  };

  const [sendApplication, { loading }] = useMutation(SEND_APPLICATION, {
    onError: () => {
      setSuccess(false);
      setError(true);
    },
    onCompleted: (data) => {
      if (data.customCareerApplicationSendEmail === true) {
        setInputs({});
        setCv(null);
        if (fileInput.current) {
          fileInput.current.value = '';
        }
        setMissCv(false);
        setSuccess(true);
        setError(false);
      } else {
        setSuccess(false);
        setError(true);
      }
    }
  });

  const submitForm = (e) => {
    e.preventDefault();

    // the file input is hidden, so its required attribute never fires - say it here
    if (!cv) {
      setMissCv(true);
      return;
    }

    setMissCv(false);
    setSuccess(false);
    setError(false);

    sendApplication({
      variables: {
        name: inputs.name,
        email: inputs.email,
        motivation: inputs.motivation || '',
        cv,
        website
      }
    });
  };

  return (
    <Section backgroundColor="#fff">
      <Wrapper both noHeight>
        <Container lg>
          <Content>
            {title &&
              <Header>
                <Intro
                  centerContainer
                  titleSize="H3"
                  title={title}
                  titleColor="#35383B"
                  titleTextAlign="center"
                  titleMaxWidth="640px"
                />
              </Header>
            }
            <Form onSubmit={submitForm}>
              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: nameLabel }} />
                <Margin />
                <Input type='text' name='name' value={inputs.name || ''} onChange={handleInput} required />
              </Field>
              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: emailLabel }} />
                <Margin />
                <Input type='email' name='email' value={inputs.email || ''} onChange={handleInput} required />
              </Field>
              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: cvLabel }} />
                <Margin />
                <FileRow>
                  <FileButton type='button' onClick={() => fileInput.current && fileInput.current.click()}>
                    <Text14 color="#5D6368" dangerouslySetInnerHTML={{ __html: cvPlaceholder }} />
                  </FileButton>
                  <FileName color="#5D6368" title={cv ? cv.name : ''}>
                    {cv ? cv.name : <span dangerouslySetInnerHTML={{ __html: cvEmptyLabel }} />}
                  </FileName>
                </FileRow>
                <HiddenFile
                  ref={fileInput}
                  type='file'
                  name='cv'
                  accept='.pdf,.doc,.docx,.odt'
                  onChange={handleFile}
                />
                {missCv &&
                  <>
                    <Margin />
                    <StatusText color="#D14343" dangerouslySetInnerHTML={{ __html: cvRequiredText || 'Carregue o seu CV para enviar a candidatura.' }} />
                  </>
                }
              </Field>
              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: motivationLabel }} />
                <Margin />
                <Textarea name='motivation' value={inputs.motivation || ''} maxLength={3000} onChange={handleInput} />
              </Field>

              <Honeypot
                type='text'
                name='website'
                tabIndex={-1}
                autoComplete='off'
                value={website}
                onChange={(event) => setWebsite(event.target.value)}
                aria-hidden='true'
              />

              {infoText &&
                <>
                  <Margin margin={2} />
                  <InfoText color="#B2B6B9" dangerouslySetInnerHTML={{ __html: infoText }} />
                </>
              }

              {/* No consent checkbox: document 1 gives the legal basis as
                  consent "dado aquando da submissão do seu CV", so sending the
                  form is the consent act and a tickbox would ask twice. */}

              <Margin margin={4.5} />

              {!loading &&
                <ButtonSubmit color='368D67' type='submit'>
                  <svg>
                    <rect x="0" y="0" fill="none" width="100%" height="100%" />
                  </svg>
                  <Text14 color="#368D67" dangerouslySetInnerHTML={{ __html: sendLabel }} />
                </ButtonSubmit>
              }
              {loading &&
                <LoadingWrap>
                  <Loading small isLoading={loading} />
                </LoadingWrap>
              }

              {success &&
                <>
                  <Margin margin={2} />
                  <StatusText color="#368D67" dangerouslySetInnerHTML={{ __html: successText || 'Candidatura enviada com sucesso.' }} />
                </>
              }
              {error &&
                <>
                  <Margin margin={2} />
                  <StatusText color="#D14343" dangerouslySetInnerHTML={{ __html: errorText || 'Ocorreu um erro ao enviar a candidatura. Por favor tente novamente.' }} />
                </>
              }
            </Form>
          </Content>
        </Container>
      </Wrapper>
    </Section>
  );
}

const Margin = styled.div`
  margin-top: ${({ margin }) => margin ? margin * 8 : 8}px;
`;
const Content = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
`;
const Header = styled.div`
  width: 100%;
`;
const Form = styled.form`
  width: 440px;
  max-width: 100%;
  margin-top: 36px;
`;
const Field = styled.div`
  margin-top: 16px;
  :first-child{
    margin-top: 0;
  }
`;
const Label = styled(Text10)`
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;
const Input = styled.input`
  width: 440px;
  max-width: 100%;
  height: 52px;
  background: #EBECED;
  border: 1px solid #EBECED;
  border-radius: 2px;
  padding: 12px 16px;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 17px;
  color: #5D6368;
  font-family: 'InterRegular', sans-serif;
  transition: all 0.4s ease-in-out;
  :hover{
    cursor: pointer;
    border: 1px solid #B2B6B9;
  }
  :focus{
    border: 1px solid #B2B6B9;
  }
`;
const Textarea = styled.textarea`
  width: 440px;
  max-width: 100%;
  height: 104px;
  background: #EBECED;
  border: 1px solid #EBECED;
  border-radius: 2px;
  padding: 12px 16px;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 17px;
  color: #5D6368;
  font-family: 'InterRegular', sans-serif;
  transition: all 0.4s ease-in-out;
  resize: vertical;
  :hover{
    cursor: pointer;
    border: 1px solid #B2B6B9;
  }
  :focus{
    border: 1px solid #B2B6B9;
  }
`;
const FileRow = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;
const FileButton = styled.button`
  height: 52px;
  padding: 0 16px;
  background: #EBECED;
  border: 1px solid #B2B6B9;
  border-radius: 2px;
  transition: all 0.4s ease-in-out;
  :hover{
    cursor: pointer;
    border-color: #5D6368;
  }
`;
const FileName = styled(Text14)`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const HiddenFile = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;
const Honeypot = styled.input`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
`;
const InfoText = styled(Text12)`
  font-size: 16px;
  line-height: 22px;
  p{
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0;
    margin-inline-end: 0;
  }
  a{
    text-decoration: underline;
    color: #5D6368;
  }
`;
const ButtonSubmit = styled.button`
  margin: 0 auto;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  height: 52px;
  width: 176px;
  box-sizing: border-box;
  transition: all 0.5s ease-in-out;
  background: transparent;
  border: 0;
  svg {
    height: 52px;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }
  rect {
    fill: none;
    stroke: ${({ color }) => color ? '#' + color : '#368D67'};
    stroke-width: 1;
    stroke-dasharray: 422, 0;
    transition: all 0.5s ease-in-out;
  }
  :hover{
    cursor: pointer;
    rect {
      stroke-width: 1;
      stroke-dasharray: 35, 310;
      stroke-dashoffset: 48;
      transition: all 1.35s cubic-bezier(0.19, 1, 0.22, 1);
    }
  }
`;
const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
`;
const StatusText = styled(Text16)`
  text-align: center;
`;
